import {
  AlertModalType,
  AlertModalTypeEnum,
  Button,
  CheckboxInput,
  Flex,
  FlexColumn,
  SecondaryButton,
  SendIcon,
  useAlertModal,
  useStyledTheme,
  WarningIcon,
} from '@equifinance/shared';
import { useState } from 'react';
import styled from 'styled-components';
import Markdown from 'react-markdown';

const ModalButtons = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[4]};
  justify-content: end;
`}`;

const ModalContainer = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[4]};
`}`;

const ModalLabel = styled.p`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
`}`;

const ModalContent = styled.div`${({ theme }) => `
  position: relative;
  border-radius: ${theme.borderRadius[2]};
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: ${theme.padding[2]};
    bottom: 0;
    opacity: 0.4;
    background: linear-gradient(180deg, ${theme.colours.neutral[9]} 0%, ${theme.colours.neutral[6]} 100%);
    height: ${theme.padding[2]};
  }
`}`;

const SubmitHtmlContent = styled.div`${({ theme }) => `
  background: ${theme.colours.neutral[9]};
  padding: ${theme.padding[5]};
  overflow: auto;
  max-height: ${theme.submitContentMaxHeight};

  ul, ol {
    padding-left: ${theme.padding[5]};
    margin: 0;
  }

  li:not(:last-of-type) {
    margin-bottom: ${theme.padding[4]};
  }
`}`;

export function SubmitApplicationContent({
  htmlText,
  onCancel,
  onConfirm,
}: {
  htmlText?: string,
  onCancel?: () => void,
  onConfirm?: () => void
}) {
  const [hasConsent, setHasConsent] = useState(false);
  return (
    <ModalContainer>
      <ModalLabel>
        You are about to submit the application to Equifinance. Please read and accept the declaration below to continue:
      </ModalLabel>
      <ModalContent>
        <SubmitHtmlContent>
          <Markdown>{htmlText}</Markdown>
        </SubmitHtmlContent>
      </ModalContent>
      <CheckboxInput
        id="consentToSubmit"
        value={hasConsent}
        onChange={setHasConsent}
        label={(
          <>
            By agreeing you are deemed to have read, understand and accept this declaration. If you have questions, please contact us.
          </>
        )}
      />
      <ModalButtons>
        <SecondaryButton onClick={onCancel} small>Cancel</SecondaryButton>
        <Button disabled={!hasConsent} onClick={onConfirm} small>Submit</Button>
      </ModalButtons>
    </ModalContainer>
  );
}

export default function useTransitionConfirm() {
  const { setAlertModalProps, showAlertModal, closeAlertModal } = useAlertModal();
  const theme = useStyledTheme();

  const showModal = ({
    ...modalOptions
  }: Partial<AlertModalType>) => {
    setAlertModalProps({
      isOpen: true,
      cancelLabel: 'Cancel',
      confirmLabel: 'Confirm',
      type: AlertModalTypeEnum.WARNING_IRREVERSIBLE,
      content: '',
      onClose: () => {
        closeAlertModal();
      },
      ...modalOptions,
    });
    showAlertModal();
  };

  const showCompleteApplicationModal = (onConfirm?: () => void) => {
    showModal({
      title: 'Complete application',
      content: (
        <ModalContainer>
          <ModalLabel>
            Are you sure you want to complete the application and send it to Phoebus?
          </ModalLabel>
          This action cannot be undone.
        </ModalContainer>
      ),
      titleIcon: (
        <WarningIcon
          color={theme.colours.primary[1]}
          stroke={theme.colours.primary[1]}
          fill={theme.colours.neutral[10]}
        />
      ),
      onConfirm,
      onClose: () => {
        closeAlertModal();
      },
    });
  };

  const showSubmitApplcationModal = (htmlText?: string, onConfirm?: () => void) => {
    showModal({
      title: 'Submit application',
      size: 'lg',
      content: (
        <SubmitApplicationContent
          htmlText={htmlText}
          onConfirm={() => {
            onConfirm?.();
            closeAlertModal();
          }}
          onCancel={() => {
            closeAlertModal();
          }}
        />
      ),
      titleIcon: (
        <SendIcon
          color={theme.colours.primary[1]}
        />
      ),
      showButtons: false,
    });
  };

  return {
    showCompleteApplicationModal,
    showSubmitApplcationModal,
  };
}
